import { FormField, List, Point } from '@nutrient-sdk/viewer'
import NutrientSDK from '../components/NutrientSdk'
import { getWidgets } from '../components/PdfHelpers'
import { CustomPdfScriptProps } from './CustomPdfScripts'
import { EditablePdfButtonGroup } from '@urbanx/agx-ui-components'

interface SellersDisclosureFieldPositions {
  [key: string]: Point
}

const getPoint = (x: number, y: number): Point => {
  return new NutrientSDK.Geometry.Point({
    x: x,
    y: y,
  }) as Point
}

const sellersDisclosureFieldPositions = {
  SDBuyerSignatures1SignHere: getPoint(125, 365),
  SDBuyerSignatures1Name: getPoint(125, 425),
  SDBuyerSignatures1DateSigned: getPoint(125, 485),
  SDBuyerSignatures2SignHere: getPoint(395, 365),
  SDBuyerSignatures2Name: getPoint(395, 425),
  SDBuyerSignatures2DateSigned: getPoint(395, 485),
  SDBuyerSignatures3SignHere: getPoint(125, 565),
  SDBuyerSignatures3Name: getPoint(125, 625),
  SDBuyerSignatures3DateSigned: getPoint(125, 685),
  SDBuyerSignatures4SignHere: getPoint(395, 565),
  SDBuyerSignatures4Name: getPoint(395, 625),
  SDBuyerSignatures4DateSigned: getPoint(395, 685),
} satisfies SellersDisclosureFieldPositions as SellersDisclosureFieldPositions

// When staff upload a sellers disclosure they want all the PDF fields to automatically
// be added to the PDF instead of having to add them manually.
// This script will add the fields to the PDF at predefined positions
// using the 'sellersDisclosureFieldPositions' constant above.
export async function SellersDisclosureFieldsScript({
  instance,
  buttonGroups = [],
  onFieldAdded,
}: CustomPdfScriptProps): Promise<void> {
  const pageIndex = 6 // Page 7

  if (!instance || instance.totalPageCount <= pageIndex) {
    console.warn(
      `SellersDisclosure: Instance is not valid or PDF does not have ${pageIndex + 1} pages`
    )
    return
  }

  const currentFormFields = await instance.getFormFields()

  if (checkIfAllMapped(currentFormFields, buttonGroups)) {
    return
  }

  for (const buttonGroup of buttonGroups) {
    for (const button of buttonGroup.buttons) {
      if (
        button.pdfFieldName &&
        sellersDisclosureFieldPositions[button.pdfFieldName]
      ) {
        if (
          currentFormFields.some((field) => field.name === button.pdfFieldName)
        )
          continue

        const position = sellersDisclosureFieldPositions[button.pdfFieldName]
        const widgetsToAdd = getWidgets(button, pageIndex, position)

        for (const { widget, formField } of widgetsToAdd) {
          instance.create([widget, formField])
          instance.setViewState((viewState) =>
            viewState.set(
              'interactionMode',
              NutrientSDK.InteractionMode.FORM_CREATOR
            )
          )

          onFieldAdded?.(widget)
        }
      }
    }
  }

  instance.setViewState((viewState) => {
    return viewState.set('currentPageIndex', pageIndex)
  })
}

function checkIfAllMapped(
  currentFields: List<FormField>,
  buttonGroups: EditablePdfButtonGroup[]
) {
  const mappedFields = new Set(currentFields.map((field) => field.name))

  for (const buttonGroup of buttonGroups) {
    for (const button of buttonGroup.buttons) {
      if (button.pdfFieldName && !mappedFields.has(button.pdfFieldName)) {
        return false
      }
    }
  }

  return true
}
